import { Button } from '@monorepo/shared/componentsV2/Button';
import { ButtonsGroup } from '@monorepo/shared/componentsV2/ButtonsGroup';
import { EmptyState } from '@monorepo/shared/componentsV2/EmptyState';
import { Header } from '@monorepo/shared/componentsV2/Header';
import { Loading } from '@monorepo/shared/componentsV2/Loading';
import { Main } from '@monorepo/shared/componentsV2/Main';
import { Search } from '@monorepo/shared/componentsV2/Search';
import { useSearchParamState } from '@monorepo/shared/hooks/navigation/useSearchParamState';
import { useHasProjectUserUpdatePermissions } from '@monorepo/shared/hooks/permissions/useHasPermissions';
import { useCurrentProject } from '@monorepo/shared/hooks/useCurrentProject';
import {
  fillHeightAndScrollable,
  page,
  verticalLayout,
} from '@monorepo/shared/styles/layout';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useProjectUsers } from '../hooks/useProjectUsers';
import { InviteUserModal } from './InviteUserModal';
import { UsersTable } from './UsersTable';

const Page = styled.div`
  ${verticalLayout}
  ${page}
`;

const StyledMain = styled(Main)`
  ${fillHeightAndScrollable}
  ${verticalLayout}
`;

const SearchContainer = styled.div`
  max-width: 10rem;
`;

export function UsersPage() {
  const { organizationId, projectId } = useParams();
  if (!organizationId || !projectId) {
    throw new Error(
      "UsersPage was rendered with a url that doesn't have organizationId or projectId",
    );
  }

  const { project } = useCurrentProject();
  const { users, isLoading, isError } = useProjectUsers({ projectId });
  const canInvite = useHasProjectUserUpdatePermissions(projectId);
  const areUsersLoaded = !isLoading && !isError && !!users;
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

  const [_filter, setFilter] = useSearchParamState<string>('filter');
  const filter = _filter ?? '';

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilter(e.target.value);
  };

  const openInviteModal = () => setIsInviteModalOpen(true);
  const closeInviteModal = () => setIsInviteModalOpen(false);

  let content;
  if (isLoading) {
    content = <Loading />;
  } else if (isError) {
    content = <EmptyState primaryText="There was a problem loading users." />;
  } else {
    content = (
      <UsersTable
        organizationId={organizationId}
        projectId={projectId}
        users={users}
        filter={filter}
      />
    );
  }

  return (
    <Page>
      <Header
        title="Users"
        subtitle={project?.name}
        variant="noDropShadow"
        rightActions={
          <ButtonsGroup>
            <SearchContainer>
              <Search onChange={handleSearch} value={filter} />
            </SearchContainer>
            {canInvite && (
              <Button
                color="primary"
                disabled={!areUsersLoaded}
                onClick={openInviteModal}
                variant="contained"
              >
                Invite new user
              </Button>
            )}
          </ButtonsGroup>
        }
      />
      <StyledMain>{content}</StyledMain>
      {isInviteModalOpen && project?.name && areUsersLoaded && (
        <InviteUserModal
          organizationId={organizationId}
          projectId={projectId}
          projectName={project.name}
          projectUsers={users}
          onClose={closeInviteModal}
        />
      )}
    </Page>
  );
}
